import React, { useMemo } from 'react';
import SheetModal, { sheetButtons } from '../common/SheetModal';
import OrderTimeline from '../common/OrderTimeline';
import CancellationFeeClaim from './CancellationFeeClaim';
import { useOrderTimelineData } from '../../hooks/useOrderTimelineData';
import { buildOrderTimeline } from '../../utils/orderTimeline';
import { fullAddress } from '../../utils/orderCardFormat';
import { orderTitle } from '../../utils/orderTitle';
import { isCarrierOrder, withoutAgreedPrice } from '../../utils/carrierOrder';
import { COLORS, FONTS } from '../../styles/tokens';

const STATUS_TEXT = {
  matched:           'Klar for henting',
  in_progress:       'Under transport',
  delivered_pending: 'Venter på bekreftelse',
  completed:         'Fullført',
  invoiced:          'Fullført',
  refunded:          'Refundert',
  disputed:          'I tvist',
  cancelled:         'Kansellert av bestiller',
};

const kr = n => Number(n).toLocaleString('nb-NO');
const fmt = d => d.toLocaleString('nb-NO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

// Skrivebeskyttet oppdragsdetalj for sjåførens fullførte og kansellerte oppdrag: adresser, utbetaling
// og tidslinje med bilder, veieseddel og signatur. Sjåføren ser bare sitt eget bud og egne fakturaer.
export default function OrderHistorySheet({ order, onClose }) {
  const scheduledDate = order.scheduledAt?.toDate?.();
  const history = useOrderTimelineData(order.id, 'driver');
  const events = useMemo(
    () => buildOrderTimeline({ order: withoutAgreedPrice(order), bids: history.bids, invoice: history.invoice, creditNotes: history.creditNotes, role: 'driver' }),
    [order, history.bids, history.invoice, history.creditNotes]
  );

  return (
    <SheetModal onClose={onClose} label="Oppdragsdetaljer">
      <div style={styles.meta}>#{order.id.slice(-6)}</div>
      <h2 style={sheetButtons.title}>{orderTitle(order)}</h2>
      <Detail label="Henting" value={fullAddress(order.pickupAddress)} />
      <Detail label="Levering" value={fullAddress(order.deliveryAddress)} />
      <Detail label="Status" value={isCarrierOrder(order) && order.status === 'cancelled' ? 'Kansellert av firmaet' : (STATUS_TEXT[order.status] ?? order.status)} />
      {scheduledDate && <Detail label="Planlagt" value={fmt(scheduledDate)} />}
      {order.driverPayout != null && <Detail label="Utbetaling" value={`kr ${kr(order.driverPayout)}`} big />}
      {order.status === 'cancelled' && <CancellationFeeClaim order={order} />}
      <OrderTimeline events={events} role="driver" loading={history.loading} />
      <button style={{ ...sheetButtons.ghost, marginTop: 6 }} onClick={onClose}>Lukk</button>
    </SheetModal>
  );
}

function Detail({ label, value, big }) {
  return (
    <div style={styles.detail}>
      <span style={{ color: COLORS.textMid }}>{label}</span>
      <b style={big ? { fontFamily: FONTS.display, fontSize: 28, lineHeight: 1 } : { textAlign: 'right' }}>{value}</b>
    </div>
  );
}

const styles = {
  meta:   { fontFamily: 'var(--font-mono)', fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase' },
  detail: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '11px 0', borderTop: `1px solid ${COLORS.border}`, fontSize: 14, color: COLORS.textDark },
};
